// Admin console: the landing screen. It answers two questions and nothing
// else — "what is the state of the platform" and "what is coming up (or is
// already late)". Every figure is live; nothing here is a placeholder.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import type { UpcomingEvent } from '../api/types';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Badge, Card, CardHead, Stat } from '../components/ui';
import {
  IconArchive,
  IconAudit,
  IconCheck,
  IconRefresh,
  IconUsers,
  IconWarning,
} from '../components/Icons';

/** Relative "in 3 h" / "2 d overdue" — the number an operator reads first. */
function relative(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const mins = Math.round(Math.abs(diff) / 60000);
  const unit =
    mins < 60
      ? t('admin.overview.inMinutes', { count: mins })
      : mins < 1440
        ? t('admin.overview.inHours', { count: Math.round(mins / 60) })
        : t('admin.overview.inDays', { count: Math.round(mins / 1440) });
  return diff < 0 ? t('admin.overview.overdueBy', { rel: unit }) : unit;
}

const KIND_ICON: Record<UpcomingEvent['kind'], React.ReactNode> = {
  sync: <IconRefresh width={14} height={14} />,
  commit: <IconArchive width={14} height={14} />,
  verification: <IconAudit width={14} height={14} />,
};

export function AdminOverview() {
  const { t } = useTranslation();
  const lang = useLang();
  const dash = useAsync(() => adminApi.adminDashboard(), []);
  const alerts = useAsync(() => adminApi.adminAlerts(), []);

  return (
    <>
      <PageHeader title={t('admin.overview.title')} subtitle={t('admin.overview.subtitle')} />

      <ApiState
        status={dash.status}
        error={dash.error}
        data={dash.status === 'error' ? null : dash.data}
        onRetry={dash.reload}
      >
        {(d) => {
          const overdue = d.upcoming.filter((u) => u.overdue).length;
          return (
            <div className="stack">
              <div className="grid grid--stats">
                <Stat label={t('admin.overview.customers')} value={formatNumber(d.totals.customers, lang)} icon={<IconUsers width={16} height={16} />} />
                <Stat label={t('admin.customers.colBuckets')} value={formatNumber(d.totals.buckets, lang)} icon={<IconArchive width={16} height={16} />} />
                <Stat label={t('admin.overview.stored')} value={formatBytes(d.totals.bytes, lang)} />
                <Stat label={t('admin.overview.objects')} value={formatNumber(d.totals.objects, lang)} />
                <Stat label={t('admin.overview.scheduled')} value={formatNumber(d.totals.scheduledBuckets, lang)} icon={<IconRefresh width={16} height={16} />} />
                <Stat label={t('admin.overview.snapshots')} value={formatNumber(d.totals.snapshots, lang)} />
              </div>

              <Card>
                <CardHead title={t('admin.overview.upcoming')}>
                  {overdue > 0 ? (
                    <Badge tone="danger">
                      <IconWarning width={14} height={14} /> {t('admin.overview.overdueCount', { count: overdue })}
                    </Badge>
                  ) : (
                    <Badge tone="success">{t('admin.overview.allOnTrack')}</Badge>
                  )}
                </CardHead>
                {d.upcoming.length === 0 ? (
                  <EmptyState title={t('admin.overview.noUpcoming')} icon={<IconCheck width={26} height={26} />} />
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>{t('admin.overview.colWhen')}</th>
                          <th>{t('admin.overview.colWhat')}</th>
                          <th>{t('admin.buckets.colCustomer')}</th>
                          <th>{t('admin.buckets.colBucket')}</th>
                          <th>{t('admin.overview.colDate')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.upcoming.map((u, i) => (
                          <tr key={`${u.kind}:${u.tenantId}:${u.archiveId ?? ''}:${i}`}>
                            <td>
                              <Badge tone={u.overdue ? 'danger' : 'default'}>{relative(u.at, t)}</Badge>
                            </td>
                            <td>
                              <span className="row" style={{ gap: 6 }}>
                                {KIND_ICON[u.kind]}
                                {t(`admin.overview.kind.${u.kind}`)}
                              </span>
                            </td>
                            <td>
                              <Link to={`/customers/${encodeURIComponent(u.tenantId)}`}>{u.tenantName}</Link>
                            </td>
                            <td>{u.bucket ?? <span className="muted">—</span>}</td>
                            <td className="muted">{formatDateTime(u.at, lang)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card>
                <CardHead title={t('admin.overview.alerts')} />
                <ApiState
                  status={alerts.status}
                  error={alerts.error}
                  data={alerts.status === 'error' ? null : alerts.data}
                  onRetry={alerts.reload}
                >
                  {(a) =>
                    a.items.length === 0 ? (
                      <EmptyState title={t('admin.overview.alertsEmpty')} icon={<IconCheck width={26} height={26} />} />
                    ) : (
                      <div className="table-wrap">
                        <table className="data">
                          <thead>
                            <tr>
                              <th>{t('common.name')}</th>
                              <th className="num">{t('admin.overview.colUsage')}</th>
                              <th className="num">{t('admin.overview.colPercent')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.items.map((x) => (
                              <tr key={x.tenantId}>
                                <td className="cell-strong">
                                  <Link to={`/customers/${encodeURIComponent(x.tenantId)}`}>{x.tenantName}</Link>
                                </td>
                                <td className="num">
                                  {formatBytes(x.usedBytes, lang)} / {formatBytes(x.limitBytes, lang)}
                                </td>
                                <td className="num">
                                  <Badge tone={x.percent >= 1 ? 'danger' : 'warning'}>
                                    {(x.percent * 100).toFixed(0)}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </ApiState>
              </Card>

              <div className="grid" style={{ gridTemplateColumns: 'minmax(240px, 1fr) minmax(320px, 2fr)', gap: 16 }}>
                <Card>
                  <CardHead title={t('admin.overview.planMix')} />
                  <div className="table-wrap">
                    <table className="data">
                      <tbody>
                        {Object.entries(d.subscriptionsByPlan).map(([plan, n]) => (
                          <tr key={plan}>
                            <td className="cell-strong">{plan}</td>
                            <td className="num">{formatNumber(n, lang)}</td>
                          </tr>
                        ))}
                        {Object.entries(d.subscriptionsByStatus).map(([status, n]) => (
                          <tr key={`s:${status}`}>
                            <td>
                              <Badge tone={status === 'active' ? 'success' : 'warning'}>
                                {t(`admin.status.${status}`, status)}
                              </Badge>
                            </td>
                            <td className="num">{formatNumber(n, lang)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card>
                  <CardHead title={t('admin.overview.recent')}>
                    <Link className="btn btn--ghost btn--sm" to="/users">
                      {t('nav.adminUsers')}
                    </Link>
                  </CardHead>
                  {d.recentActivity.length === 0 ? (
                    <EmptyState title={t('audit.empty')} />
                  ) : (
                    <div className="table-wrap">
                      <table className="data">
                        <tbody>
                          {d.recentActivity.map((e, i) => (
                            <tr key={i}>
                              <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                                {formatDateTime(e.at, lang)}
                              </td>
                              <td className="cell-strong">{e.actor}</td>
                              <td>
                                <Badge tone={e.result && e.result !== 'OK' ? 'danger' : 'default'}>{e.action}</Badge>
                              </td>
                              <td className="muted" style={{ wordBreak: 'break-all' }}>
                                {e.resource}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          );
        }}
      </ApiState>
    </>
  );
}
